import React, { useState } from 'react';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import {
  Stack,
  Tooltip,
  IconButton,
  CircularProgress,
  Box,
  Chip,
  useMediaQuery,
  useTheme,
  Button,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { LoadingMore, NewDialogActions, NewDialogContent, NewDialogTitle } from 'components';

import Model from '../Model';
import Filter from '../Filter';
import Delete from '../Delete';
import Create from '../Create';
import Assignment from '../Assignment';
import { FormattedMessage } from 'react-intl';

import { useSelector } from 'react-redux';
import { hasRequiredRole } from 'helpers';
import { fCurrency } from 'helpers/formatNumber';
import { TariffModel } from 'models';

export default function List({
  page,
  limit,
  total,
  report: {
    total_houre,
    total_service,
    total_shift,
    total_tonnage,
    total_tonnage_service,
    all_tonnage_service,
    total_cost,
    all_tonnage,
    total_cubic_meter,
  } = {},
  fetchReport,
  reportLoading,
  setPage,

  result,
  refetch,
  loading,

  title,
  isPopup,
  onClose,

  isAssign,
  onAssign,
  assigning,
  multiSelect,

  filter,
  setFilter,
  clearFilter,
  preSelected,
}) {
  const [tariffTitle, setTariffTitle] = useState('');
  const [statsOpen, setStatsOpen] = useState(false);

  const [selected, setSelected] = useState(preSelected || []);
  const [direction, setDirection] = useState('right');
  const { isAuthenticated, userInfo } = useSelector((state) => state.auth);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const canSeeStats =
    isAuthenticated &&
    hasRequiredRole(
      ['superadmin', 'operator', 'companyCeo', 'companyOperator', 'companyFinancial', 'workshopSupervisor'],
      userInfo?.roles
    );
  const summaryButtonSx = {
    px: 2,
    minHeight: { xs: 40, sm: 36 },
    height: { xs: 40, sm: 36 },
    width: { xs: '100%', sm: 'auto' },
    minWidth: { sm: 150 },
    whiteSpace: 'nowrap',
  };

  const handleSelect = (item) => {
    const selectedIndex = selected.map((item) => item.id).indexOf(item.id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
    if (isAssign && !multiSelect && newSelected.length > 0) {
      onAssign(newSelected, onClose);
    }
  };

  const getColor = (index) => {
    return index % 2 === 0 ? false : true;
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(result);
      return;
    }
    setSelected([]);
  };

  const clearSelection = () => setSelected([]);

  const refresh = () => {
    clearSelection();
    refetch();
  };

  const openStats = async () => {
    setStatsOpen(true);
    if (fetchReport) {
      await fetchReport();
    }
  };

  const onAssignTariff = (data, onClose) => {
    const tariff = data[0];
    setTariffTitle(tariff?.title);
    setFilter({
      site_ids: tariff?.site_id,
      workshop_ids: tariff?.workshop_id,
      material_type_ids: tariff?.material_type_id,
      shift_type_ids: tariff?.shift_type_id,
      operation_type_ids: tariff?.operation_type_id,
    });
    onClose();
  };

  const totalAmounts = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 1,
        pt: 1,
      }}
    >
      {total && <Chip label={`کل فعالیت‌ها : ${fCurrency(total)}`} />}
      {total_houre && <Chip label={`جمع ساعت : ${fCurrency(total_houre)}`} />}
      {total_shift && <Chip label={`جمع شیفت : ${fCurrency(total_shift)}`} />}
      {total_service && <Chip label={`جمع سرویس : ${fCurrency(total_service)}`} />}
      {total_tonnage_service && <Chip label={`تبدیل شده تناژی : ${fCurrency(total_tonnage_service)}`} />}
      {all_tonnage_service && <Chip label={`کل تبدیل شده تناژی : ${fCurrency(all_tonnage_service)}`} />}
      {total_tonnage && <Chip label={`تناژ قرادادی : ${fCurrency(total_tonnage)}`} />}
      {all_tonnage && <Chip label={`کل تناژ : ${fCurrency(all_tonnage)}`} />}
      {total_cubic_meter && <Chip label={`جمع مترمکعب : ${fCurrency(total_cubic_meter)}`} />}
      {total_cost && <Chip label={`جمع هزینه‌ها : ${fCurrency(total_cost)}`} />}
    </Box>
  );

  const tariffActionButton = (
    <TariffModel isPopup isAssign onAssign={onAssignTariff}>
      <Button color="primary" variant="contained" fullWidth={isMobile} sx={summaryButtonSx}>
        انتخاب تعرفه
        {!isMobile && tariffTitle && (
          <Typography component="span" variant="caption" sx={{ mx: 1, color: 'inherit', opacity: 0.8 }}>
            {tariffTitle}
          </Typography>
        )}
      </Button>
    </TariffModel>
  );

  const statsActionButton = canSeeStats ? (
    <Button color="primary" variant="contained" fullWidth={isMobile} onClick={openStats} sx={summaryButtonSx}>
      مشاهده آمار کل
    </Button>
  ) : null;

  return (
    <>
      {isAssign && multiSelect && (
        <Assignment
          title={title}
          onClose={onClose}
          onAssign={onAssign}
          selected={selected}
          assigning={assigning}
          handleSelect={handleSelect}
        />
      )}
      <NewDialogTitle title={title} onClose={onClose} isPopup={isPopup}>
        <Stack spacing={1}>
          <Filter
            total={total}
            init={filter}
            ids={selected}
            refetch={refetch}
            loading={loading}
            setFilter={setFilter}
            clearFilter={clearFilter}
            acception={result.length}
            selection={selected.length}
            clearSelection={clearSelection}
            handleSelectAllClick={handleSelectAllClick}
            isPopup={isPopup}
            desktopActions={
              !isMobile && (
                <>
                  {/* {tariffActionButton} */}
                  {statsActionButton}
                </>
              )
            }
          />
          {isMobile && (
            <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
              {/* {tariffActionButton} */}
              {statsActionButton}
            </Stack>
          )}
        </Stack>
      </NewDialogTitle>
      <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>آمار کل فعالیت‌ها</DialogTitle>
        <DialogContent>
          {reportLoading ? (
            <Stack alignItems="center" justifyContent="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : (
            totalAmounts
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={() => setStatsOpen(false)}>
            بستن
          </Button>
        </DialogActions>
      </Dialog>
      <NewDialogContent>
        <Stack p={0.5} rowGap={0.5} minHeight={isPopup ? 'calc(100vh - 245px)' : 'calc(100vh - 264px)'}>
          {result.map((model, i) => (
            <Model
              selectOnClick={isAssign}
              key={i}
              model={model}
              refetch={refetch}
              style={{ flex: 1 }}
              direction={direction}
              delay={i + 1}
              color={getColor(i)}
              checked={selected.find((select) => model.id === select.id) ? true : false}
              handleSelect={() => handleSelect({ id: model?.id, title: model.title })}
            />
          ))}
          <LoadingMore total={total} result={result.length} loading={loading} onClick={() => setPage(page + 1)} />
        </Stack>
      </NewDialogContent>
      {/* <Box sx={{ position: 'sticky', bottom: 16, left: 16 }}>
        <Fab color="primary" aria-label="add">
          <AddIcon />
        </Fab>
      </Box> */}
      <NewDialogActions isPopup={isPopup}>
        <Tooltip title={<FormattedMessage id="refresh" />}>
          <IconButton sx={{ bgcolor: 'action.selected', color: '#fff' }} size="medium" onClick={refresh}>
            {loading ? (
              <CircularProgress color="primary" size={25} />
            ) : (
              <RefreshRoundedIcon color="primary" fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        {/* <Box sx={{ '& > :not(style)': { position: 'fixed', bottom: 16, left: 16 } }}>
          <Fab color="primary" aria-label="add">
            <AddIcon />
          </Fab>
        </Box> */}
        {/* <Delete ids={selected.map((item) => item.id)} refetch={refresh} selection={selected.length > 0} />
        <Create title={<FormattedMessage id="create" />} refetch={refresh} /> */}
        {isAuthenticated &&
          hasRequiredRole(
            ['superadmin', 'operator', 'companyCeo', 'companyOperator', 'companyFinancial', 'workshopSupervisor'],
            userInfo?.roles
          ) && (
            <>
              {!isPopup && (
                <Delete ids={selected.map((item) => item.id)} refetch={refresh} selection={selected.length > 0} />
              )}
              <Create title={<FormattedMessage id="create" />} refetch={refresh} />
            </>
          )}
      </NewDialogActions>
    </>
  );
}
